import { skipToken, useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useInteractOutside } from 'react-aria';

import { OrganizationFeature, type Organization } from '@gitbook/api-client';
import type { ClickEventAction } from '@gitbook/app-analytics/client';
import * as analytics from '@gitbook/app-analytics/client';
import { isPricingFeatureAvailable } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import { LoadingPanel, TextInput } from '@gitbook/spine-react';
import { useDebouncedState, useEventCallback } from '@gitbook/util-react-hooks';

import { shortcuts, useKeyboardShortcuts } from '../../Keyboard';
import {
    useDeferredSearchQuery,
    useNavigationState,
    useSetAnalyticsScreen,
} from '../../navigation';
import { apiQuery, cancelOrganizationSearch } from '../../state';
import { useAISearchQuery } from '../AISearch';
import { SearchModal, SearchModalFooter, type SearchModalRef } from '../Search';
import { UpgradeDialogProvider } from '../UpgradeDialog';
import { AppSearchAIFooter } from './AppSearchAIFooter';
import { AppSearchBody } from './Search';

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Global component to show the search modal when opened.
 */
export function AppSearch() {
    const [query, { open, close, setQuery }, askAI] = useDeferredSearchQuery();

    useKeyboardShortcuts(
        {
            [shortcuts.SEARCH_OPEN]: () => {
                open();
            },
        },
        ['application', 'editor-selection-collapsed']
    );

    if (typeof query === 'string') {
        return (
            <React.Suspense fallback={null}>
                <AppSearchModal query={query} setQuery={setQuery} close={close} askAI={askAI} />
            </React.Suspense>
        );
    }
    return null;
}

function AppSearchModal(props: {
    query: string;
    setQuery: (query: string) => void;
    close: () => void;
    askAI: boolean;
}): React.ReactElement {
    const { query, setQuery, close, askAI } = props;
    const [pendingSearchQuery, searchQuery, setSearchQuery] = useDebouncedState(query, {
        delay: SEARCH_DEBOUNCE_MS,
    });

    const searchRef = React.useRef<SearchModalRef>(null);
    const [aiQuery, setAIQuery] = useAISearchQuery();
    const [usingAI, setUsingAI] = React.useState(askAI);

    // Lookup current organization
    const { screen, overlay } = useNavigationState();
    const currentScreen = overlay || screen;

    const organizationId = 'organization' in currentScreen ? currentScreen.organization : undefined;

    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', organizationId ? { organizationId } : skipToken)
    );

    const aiEnabled =
        !!organization &&
        isPricingFeatureAvailable(organization, { featureKey: OrganizationFeature.AiLens });

    /**
     * User is submitting a query to the AI.
     */
    const onSubmitAIQuery = useEventCallback(
        (newQuery: string, trigger?: 'click_ask_gitbook' | 'press_enter' | 'query_param') => {
            if (!aiEnabled || !organization) {
                return;
            }

            cancelOrganizationSearch(organization.id);
            setUsingAI(true);
            setQuery(newQuery);
            setSearchQuery(newQuery, { debounce: false });
            setAIQuery(newQuery);
            searchRef.current?.focus();

            analytics.logEvent({
                type: 'ai_search_submit',
                organization: organization.id,
                query: newQuery,
                trigger: trigger ?? 'press_enter',
            });
        }
    );

    /**
     * User is clicking somewhere that updates the query.
     */
    const onUpdateQuery = (newQuery: string) => {
        if (organization) {
            cancelOrganizationSearch(organization.id);
        }
        setQuery(newQuery);
        setSearchQuery(newQuery);
        searchRef.current?.focus();
    };

    /**
     * User is changing the query, so we update the Query and also revert back to search-view.
     */
    const onQueryChange = (query: string) => {
        if (organization) {
            cancelOrganizationSearch(organization.id);
        }
        setUsingAI(false);
        setQuery(query);
        setSearchQuery(query);
    };

    React.useEffect(() => {
        if (askAI) {
            if (query && query !== aiQuery.query) {
                onSubmitAIQuery(query, 'query_param');
            }
        }
    }, [query, askAI, aiQuery.query, onSubmitAIQuery]);

    /*
     * We use searchType to differentiate whether an ai search has been triggered by deep linking with the ask_ai=true query param
     */
    useSetAnalyticsScreen(
        {
            screen: 'search',
            searchType: askAI ? 'ai' : 'search',
            organization: organizationId,
        },
        [askAI, organizationId]
    );

    return (
        <SearchModal
            ref={searchRef}
            inputIcon={aiEnabled ? Icon.SearchOrAsk : Icon.Search}
            query={query}
            onQueryChange={onQueryChange}
            onSubmitQuery={onSubmitAIQuery}
            onClose={close}
            queryPlaceholder={aiEnabled ? 'Ask or search...' : 'Search content...'}
            enableSearchAIPrompt={
                organization ? (
                    <UpgradeDialogProvider>
                        <AppSearchAIFooter organization={organization} />
                    </UpgradeDialogProvider>
                ) : null
            }
        >
            <AppSearchBody
                query={query}
                searchQuery={pendingSearchQuery === searchQuery ? searchQuery : ''}
                aiQuery={aiQuery}
                usingAI={usingAI}
                organization={organization}
                onUpdateQuery={onUpdateQuery}
                onSubmitAIQuery={onSubmitAIQuery}
            />
        </SearchModal>
    );
}

/**
 * Analytics configuration for the inline app search input.
 */
export type AppSearchInlineAnalytics = {
    screen: analytics.ClickAnalyticsEvent['screen'];
    screenSection: string;
    component: string;
};

/**
 * Inline search input that reuses the global app search suggestions and results.
 */
export function AppSearchInline(props: {
    organization: Organization;
    analytics?: AppSearchInlineAnalytics;
}): React.ReactElement {
    const { analytics: analyticsConfig, organization } = props;
    const [query, searchQuery, setQuery] = useDebouncedState('', {
        delay: SEARCH_DEBOUNCE_MS,
    });
    const [isOpen, setIsOpen] = React.useState(false);
    const [aiQuery, setAIQuery] = useAISearchQuery();
    const [usingAI, setUsingAI] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    const aiEnabled = isPricingFeatureAvailable(organization, {
        featureKey: OrganizationFeature.AiLens,
    });

    function trackClickEvent(action: ClickEventAction) {
        if (!analyticsConfig) {
            return;
        }

        analytics.logEvent({
            type: 'click',
            action,
            organization: organization.id,
            ...analyticsConfig,
        });
    }

    useInteractOutside({
        ref: wrapperRef,
        onInteractOutside: () => {
            setIsOpen(false);
        },
    });

    /**
     * User is submitting a query to the AI from the inline search input.
     */
    const onSubmitAIQuery = useEventCallback(
        (newQuery: string, trigger?: 'click_ask_gitbook' | 'press_enter') => {
            if (!aiEnabled) {
                return;
            }

            cancelOrganizationSearch(organization.id);
            setIsOpen(true);
            setUsingAI(true);
            setQuery(newQuery, { debounce: false });
            setAIQuery(newQuery);
            inputRef.current?.focus();

            analytics.logEvent({
                type: 'ai_search_submit',
                organization: organization.id,
                query: newQuery,
                trigger: trigger ?? 'press_enter',
            });
        }
    );

    /**
     * User is clicking a suggestion that updates the inline search query.
     */
    const onUpdateQuery = useEventCallback((newQuery: string) => {
        cancelOrganizationSearch(organization.id);
        setIsOpen(true);
        setUsingAI(false);
        setQuery(newQuery);
        inputRef.current?.focus();
    });

    /**
     * User is typing in the inline search input.
     */
    const onQueryChange = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        cancelOrganizationSearch(organization.id);
        setIsOpen(true);
        setUsingAI(false);
        setQuery(event.currentTarget.value);
    });

    /**
     * User is pressing a key while the inline search input is focused.
     */
    const onInputKeyDown = useEventCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
            setIsOpen(false);
            return;
        }
    });

    const onSearchBlur = useEventCallback((event: React.FocusEvent<HTMLDivElement>) => {
        const nextFocusedElement = event.relatedTarget;
        if (!nextFocusedElement || !event.currentTarget.contains(nextFocusedElement)) {
            setIsOpen(false);
        }
    });

    return (
        <div
            ref={wrapperRef}
            className="relative w-full max-w-[540px]"
            onBlurCapture={onSearchBlur}
        >
            <TextInput
                ref={inputRef}
                leadingIcon={aiEnabled ? Icon.SearchOrAsk : Icon.Search}
                size="large"
                value={query}
                placeholder={aiEnabled ? 'Ask or search...' : 'Search content...'}
                onChange={onQueryChange}
                onFocus={() => {
                    setIsOpen(true);
                }}
                onKeyDown={onInputKeyDown}
                onSubmit={(value) => {
                    trackClickEvent('submit');
                    onSubmitAIQuery(value, 'press_enter');
                }}
            />

            {isOpen ? (
                <div className="absolute top-full left-0 z-(--z-index-popover) mt-2 flex max-h-[min(560px,calc(100vh-16rem))] w-full flex-col overflow-hidden rounded-lg border bg-base shadow-elevated animate-in fade-in zoom-in-95">
                    <div className="flex scrollbar flex-1 flex-col overflow-y-auto scroll-gradient-mask-y">
                        <React.Suspense fallback={<LoadingPanel size="large" />}>
                            <AppSearchBody
                                query={query}
                                searchQuery={query === searchQuery ? searchQuery : ''}
                                aiQuery={aiQuery}
                                usingAI={usingAI}
                                organization={organization}
                                onUpdateQuery={onUpdateQuery}
                                onSubmitAIQuery={onSubmitAIQuery}
                            />
                        </React.Suspense>
                    </div>
                    <SearchModalFooter
                        enableSearchAIPrompt={
                            <UpgradeDialogProvider>
                                <AppSearchAIFooter organization={organization} />
                            </UpgradeDialogProvider>
                        }
                    />
                </div>
            ) : null}
        </div>
    );
}
